import React, { useEffect, useMemo, useState } from 'react';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { INK, STATUS } from '../../Utils/styleTokens';
import { uiLabel } from '../../Utils/Labeling';
import {
  listAliasBases,
  listAliases,
  getAlias,
  saveAlias,
  deleteAlias,
} from '../api';

// Name validation mirrors the server-side rule exactly (masking worker alias service), same
// pattern as the old Custom Lookup Lists / Custom Algorithms screens.
const NAME_REGEX = /^[A-Za-z][A-Za-z0-9_]{2,63}$/;

const REPLACEMENT_MODES = ['CASE_PRESERVING', 'FULL_REPLACEMENT', 'STREET_ADDRESS_SUFFIX'];

// One value per line; blank lines and lines starting with '#' (after trim) are comments and
// are excluded from the effective value set sent to the server.
const parseEffectiveValues = (text) =>
  String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith('#'));

// Base ordering: LOOKUP bases first (the built-in replacement-pool bases admins manage most
// often, same surface the old Custom Lookup Lists screen led with), then STANDARD bases
// alphabetically, then CUSTOM_JAR bases last (one per uploaded custom algorithm — the newest
// and most niche surface). Ties within a group break alphabetically by base name.
const KIND_RANK = { LOOKUP: 0, STANDARD: 1, CUSTOM_JAR: 2 };
const sortBases = (bases) =>
  [...bases].sort((a, b) => {
    const ra = KIND_RANK[a.kind] != null ? KIND_RANK[a.kind] : 99;
    const rb = KIND_RANK[b.kind] != null ? KIND_RANK[b.kind] : 99;
    if (ra !== rb) return ra - rb;
    return String(a.name).localeCompare(String(b.name));
  });

const KIND_CHIP_STYLE = {
  LOOKUP: { background: STATUS.info.bg, color: STATUS.info.text },
  STANDARD: { background: INK[100], color: INK[700] },
  CUSTOM_JAR: { background: '#F3E8FF', color: '#7360F6' },
};

// Algorithm Aliases screen (Advanced Config). Replaces the old Custom Lookup Lists screen with
// the general alias model: every base algorithm (LOOKUP, STANDARD, or a per-jar CUSTOM_JAR base)
// can have zero or more named aliases layered on top of it. Saving an alias creates a separate
// algorithm — named after it — that appears in every algorithm dropdown across Advanced Config
// (Entity -> Algorithm Mapping) and the masking inventory, install-wide. Shipped aliases are
// read-only; only user-created aliases can be edited or removed here.
const AliasesScreen = ({ canEdit }) => {
  const [bases, setBases] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Also used as the refresh callback panels call after a successful save/delete.
  const load = async () => {
    try {
      const [baseList, aliasList] = await Promise.all([listAliasBases(), listAliases()]);
      setBases(Array.isArray(baseList) ? baseList : []);
      setAliases(Array.isArray(aliasList) ? aliasList : []);
      setError(null);
    } catch (e) {
      setError(e?.body?.message || e?.message || 'Failed to load algorithm aliases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orderedBases = useMemo(() => sortBases(bases), [bases]);
  const aliasesByBase = useMemo(() => {
    const map = {};
    aliases.forEach((a) => {
      if (!map[a.base]) map[a.base] = [];
      map[a.base].push(a);
    });
    return map;
  }, [aliases]);

  return (
    <div>
      <h1 className='adv-config-screen-title'>Algorithm Aliases</h1>
      <p className='adv-config-screen-desc'>
        Each base algorithm below ships fixed built-in behavior. Adding an alias creates a{' '}
        <strong>separate algorithm</strong> — named after it — that then appears in every
        algorithm dropdown across Advanced Config (Entity → Algorithm Mapping) and the masking
        inventory, install-wide. Shipped aliases are read-only; only aliases you create can be
        edited or removed.
      </p>

      {error && <div className='adv-config-msg-err' style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <div data-testid='alias-base-panels'>
          {orderedBases.map((base) => (
            <AliasBasePanel
              key={base.name}
              base={base}
              aliases={aliasesByBase[base.name] || []}
              canEdit={canEdit}
              onChanged={load}
            />
          ))}
          {orderedBases.length === 0 && !error && (
            <div style={{ color: INK[500] }}>No alias bases available.</div>
          )}
        </div>
      )}
    </div>
  );
};

// One expandable panel per base. Rows inside list every alias for that base; SHIPPED rows are
// display-only, USER rows get Edit/Delete (canEdit-gated).
const AliasBasePanel = ({ base, aliases, canEdit, onChanged }) => {
  const [expanded, setExpanded] = useState(false);
  // false | 'add' | <alias summary object> (edit target)
  const [formTarget, setFormTarget] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  return (
    <Accordion
      expanded={expanded}
      onChange={(e, isExpanded) => setExpanded(isExpanded)}
      data-testid={`alias-base-panel-${base.name}`}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Typography style={{ fontWeight: 600 }}>{uiLabel(base.name)}</Typography>
          <span style={{ color: INK[400], fontSize: 11 }}>{base.name}</span>
          <Chip
            size='small'
            label={base.kind}
            style={KIND_CHIP_STYLE[base.kind] || KIND_CHIP_STYLE.STANDARD}
          />
          <span style={{ color: INK[500], fontSize: 12 }}>
            {aliases.length} alias{aliases.length === 1 ? '' : 'es'}
          </span>
        </div>
      </AccordionSummary>
      <AccordionDetails style={{ flexDirection: 'column', gap: 12 }}>
        {base.description && (
          <Typography variant='body2' color='textSecondary' style={{ marginBottom: 4 }}>
            {base.description}
          </Typography>
        )}
        {base.kind === 'LOOKUP' && (
          <Typography variant='caption' color='textSecondary' style={{ marginBottom: 8, display: 'block' }}>
            The built-in list for this base is read-only. A new alias is a separate algorithm
            that will appear in every algorithm dropdown; identical values saved under a
            different alias name still produce different masked output — each alias has its own
            salt.
          </Typography>
        )}

        <table className='adv-config-table' data-testid={`alias-table-${base.name}`}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th />
              <th style={{ width: 160 }} />
            </tr>
          </thead>
          <tbody>
            {aliases.map((a) => (
              <tr key={a.name} data-testid={`alias-row-${a.name}`}>
                <td>{a.name}</td>
                <td style={{ fontSize: 12, color: INK[500] }}>{a.description}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {a.source === 'SHIPPED' && (
                      <Chip size='small' label='Shipped' data-testid={`alias-shipped-${a.name}`} />
                    )}
                    {a.safeHarbor && (
                      <Chip
                        size='small'
                        label='Safe Harbor'
                        style={{ background: STATUS.success.bg, color: STATUS.success.text }}
                      />
                    )}
                    {a.deprecated && (
                      <Chip
                        size='small'
                        label='Deprecated'
                        style={{ background: STATUS.warning.bg, color: STATUS.warning.text }}
                      />
                    )}
                  </div>
                </td>
                <td>
                  {a.source === 'USER' && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type='button'
                        className='adv-config-btn-secondary adv-config-btn'
                        data-testid={`alias-edit-${a.name}`}
                        disabled={!canEdit}
                        onClick={() => setFormTarget(a)}
                      >
                        Edit
                      </button>
                      <button
                        type='button'
                        className='adv-config-btn-secondary adv-config-btn'
                        data-testid={`alias-delete-${a.name}`}
                        disabled={!canEdit}
                        onClick={() => setDeleteTarget(a.name)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {aliases.length === 0 && (
              <tr><td colSpan={4} style={{ color: INK[500] }}>No aliases yet for this base.</td></tr>
            )}
          </tbody>
        </table>

        <div style={{ marginTop: 12 }}>
          <Button
            data-testid={`alias-add-${base.name}`}
            variant='outlined'
            size='small'
            disabled={!canEdit}
            onClick={() => setFormTarget('add')}
          >
            Add alias
          </Button>
        </div>
      </AccordionDetails>

      {formTarget && (
        <AliasFormDialog
          base={base}
          existing={formTarget === 'add' ? null : formTarget}
          onClose={() => setFormTarget(false)}
          onSaved={() => { setFormTarget(false); onChanged(); }}
        />
      )}

      {deleteTarget && (
        <AliasDeleteDialog
          name={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); onChanged(); }}
        />
      )}
    </Accordion>
  );
};

// Add/Edit form, flavored by the base's kind:
//   LOOKUP     -> name + values textarea + replacement-mode select
//   STANDARD   -> name + dynamic key/value parameter rows
//   CUSTOM_JAR -> name + key/value rows, or a raw-JSON textarea behind a toggle
const AliasFormDialog = ({ base, existing, onClose, onSaved }) => {
  const isEdit = !!existing;

  const [name, setName] = useState(existing?.name || '');
  const [description, setDescription] = useState(existing?.description || '');

  // LOOKUP fields
  const [valuesText, setValuesText] = useState('');
  const [valuesLoading, setValuesLoading] = useState(isEdit && base.kind === 'LOOKUP');
  const [replacementMode, setReplacementMode] = useState(
    (existing?.params && existing.params.replacementMode) || 'CASE_PRESERVING',
  );

  // STANDARD / CUSTOM_JAR key-value rows, seeded from the existing alias's params (summaries
  // already carry `params` per the platform contract, so no extra fetch is needed here).
  const [paramRows, setParamRows] = useState(() => {
    const params = existing?.params && typeof existing.params === 'object' ? existing.params : {};
    const rows = Object.keys(params).map((k) => ({ key: k, value: String(params[k]) }));
    return rows.length > 0 ? rows : [{ key: '', value: '' }];
  });

  // CUSTOM_JAR raw-JSON toggle
  const [rawMode, setRawMode] = useState(false);
  const [rawJson, setRawJson] = useState(
    existing?.params ? JSON.stringify(existing.params, null, 2) : '{}',
  );
  const [rawJsonError, setRawJsonError] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [nameCollision, setNameCollision] = useState(false);

  // Edit of a USER lookup alias needs its values, which summaries never carry — fetch detail.
  useEffect(() => {
    if (isEdit && base.kind === 'LOOKUP') {
      (async () => {
        try {
          const detail = await getAlias(existing.name);
          setValuesText(Array.isArray(detail?.values) ? detail.values.join('\n') : '');
        } catch (e) {
          setSubmitError(e?.body?.message || e?.message || 'Failed to load alias values');
        } finally {
          setValuesLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trimmedName = name.trim();
  const nameValid = NAME_REGEX.test(trimmedName);
  const nameInvalid = trimmedName !== '' && !nameValid;
  const effectiveValues = useMemo(() => parseEffectiveValues(valuesText), [valuesText]);

  const updateRow = (idx, field, value) => {
    setParamRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const addRow = () => setParamRows((rows) => [...rows, { key: '', value: '' }]);
  const removeRow = (idx) => setParamRows((rows) => rows.filter((_, i) => i !== idx));

  const buildParamsFromRows = () => {
    const obj = {};
    paramRows.forEach((r) => {
      const k = r.key.trim();
      if (k) obj[k] = r.value;
    });
    return obj;
  };

  const validateRawJson = () => {
    try {
      const parsed = JSON.parse(rawJson || '{}');
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setRawJsonError('Config must be a JSON object.');
        return null;
      }
      setRawJsonError(null);
      return parsed;
    } catch {
      setRawJsonError('Config must be valid JSON.');
      return null;
    }
  };

  const canSubmit =
    nameValid &&
    !valuesLoading &&
    (base.kind !== 'LOOKUP' || effectiveValues.length > 0) &&
    !(base.kind === 'CUSTOM_JAR' && rawMode && rawJsonError);

  const handleSubmit = async () => {
    setSubmitError(null);
    setNameCollision(false);

    let params;
    if (base.kind === 'LOOKUP') {
      params = { replacementMode };
    } else if (base.kind === 'CUSTOM_JAR' && rawMode) {
      const parsed = validateRawJson();
      if (!parsed) return;
      params = parsed;
    } else {
      params = buildParamsFromRows();
    }

    const payload = { base: base.name, params, description: description.trim() || undefined };
    if (base.kind === 'LOOKUP') {
      payload.values = effectiveValues;
    }

    setSubmitting(true);
    try {
      await saveAlias(trimmedName, payload);
      // Unmounts this dialog — no further state updates below on success, since setting
      // state on it afterwards would warn about updating an unmounted component.
      onSaved();
      return;
    } catch (e) {
      if (e?.status === 409) {
        setNameCollision(true);
        setSubmitError(e?.body?.message || 'That name is already in use.');
      } else {
        setSubmitError(e?.body?.message || e?.message || 'Save failed');
      }
    }
    setSubmitting(false);
  };

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth data-testid={`alias-form-dialog-${base.name}`}>
      <DialogTitle>{isEdit ? `Edit ${existing.name}` : `Add alias — ${uiLabel(base.name)}`}</DialogTitle>
      <DialogContent>
        <TextField
          inputProps={{ 'data-testid': 'alias-name-input' }}
          label='Alias name'
          placeholder='e.g. first_name_regional'
          value={name}
          disabled={isEdit}
          error={nameInvalid || nameCollision}
          helperText={
            nameInvalid
              ? 'Must start with a letter and contain only letters, numbers, and underscores (3-64 characters).'
              : (nameCollision ? 'That name is already in use.' : ' ')
          }
          onChange={(e) => { setName(e.target.value); setNameCollision(false); setSubmitError(null); }}
          fullWidth
          margin='dense'
        />

        <TextField
          inputProps={{ 'data-testid': 'alias-description-input' }}
          label='Description (optional)'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          margin='dense'
        />

        {base.kind === 'LOOKUP' && (
          <>
            {valuesLoading ? (
              <div style={{ fontSize: 13, color: INK[500], marginTop: 8 }}>Loading values…</div>
            ) : (
              <>
                <TextField
                  inputProps={{ 'data-testid': 'alias-values-input' }}
                  label='Values (one per line)'
                  placeholder={'jane\njohn\n# lines starting with # are ignored'}
                  value={valuesText}
                  onChange={(e) => setValuesText(e.target.value)}
                  multiline
                  rows={8}
                  fullWidth
                  variant='outlined'
                  margin='dense'
                />
                <div style={{ fontSize: 12, color: INK[500], margin: '6px 0 12px' }}>
                  {effectiveValues.length} effective value{effectiveValues.length === 1 ? '' : 's'}
                </div>
              </>
            )}
            <div style={{ marginTop: 8 }}>
              <Typography variant='caption' color='textSecondary' style={{ display: 'block', marginBottom: 4 }}>
                Replacement mode
              </Typography>
              <select
                className='adv-config-select'
                data-testid='alias-replacement-mode-select'
                style={{ minWidth: 260 }}
                value={replacementMode}
                onChange={(e) => setReplacementMode(e.target.value)}
              >
                {REPLACEMENT_MODES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </>
        )}

        {(base.kind === 'STANDARD' || base.kind === 'CUSTOM_JAR') && (
          <div style={{ marginTop: 12 }}>
            {base.kind === 'CUSTOM_JAR' && (
              <FormControlLabel
                control={
                  <Checkbox
                    inputProps={{ 'data-testid': 'alias-raw-json-toggle' }}
                    checked={rawMode}
                    onChange={(e) => { setRawMode(e.target.checked); setRawJsonError(null); }}
                  />
                }
                label='Raw JSON'
              />
            )}

            {base.kind === 'CUSTOM_JAR' && rawMode ? (
              <TextField
                inputProps={{ 'data-testid': 'alias-raw-json-input' }}
                label='Params (JSON)'
                value={rawJson}
                onChange={(e) => { setRawJson(e.target.value); setRawJsonError(null); }}
                onBlur={validateRawJson}
                multiline
                rows={6}
                fullWidth
                variant='outlined'
                margin='dense'
                error={!!rawJsonError}
                helperText={rawJsonError || ' '}
              />
            ) : (
              <>
                <Typography variant='body2' style={{ marginBottom: 8 }}>Parameters</Typography>
                {paramRows.map((row, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <TextField
                      inputProps={{ 'data-testid': `alias-param-key-${idx}` }}
                      placeholder='key'
                      value={row.key}
                      onChange={(e) => updateRow(idx, 'key', e.target.value)}
                      margin='dense'
                    />
                    <TextField
                      inputProps={{ 'data-testid': `alias-param-value-${idx}` }}
                      placeholder='value'
                      value={row.value}
                      onChange={(e) => updateRow(idx, 'value', e.target.value)}
                      margin='dense'
                    />
                    <Button
                      data-testid={`alias-param-remove-${idx}`}
                      size='small'
                      onClick={() => removeRow(idx)}
                      disabled={paramRows.length === 1}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button data-testid='alias-param-add' size='small' onClick={addRow}>+ Add parameter</Button>
              </>
            )}
          </div>
        )}

        {submitError && <div className='adv-config-msg-err' style={{ marginTop: 12 }}>{submitError}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          data-testid='alias-form-save'
          variant='contained'
          color='primary'
          disabled={submitting || !canSubmit}
          onClick={handleSubmit}
        >
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Delete confirm dialog. First attempt runs with force=false; a 409 with referencedBy swaps the
// dialog into a force-confirm state (same shape as the old LookupListsScreen/CustomAlgorithms
// confirm dialogs).
const AliasDeleteDialog = ({ name, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [referencedBy, setReferencedBy] = useState(null);

  const doDelete = async (force) => {
    setDeleting(true);
    setError(null);
    try {
      await deleteAlias(name, force);
      onDeleted();
      return;
    } catch (e) {
      const refs = e?.body && Array.isArray(e.body.referencedBy) ? e.body.referencedBy : null;
      if (e?.status === 409 && refs && refs.length > 0 && !force) {
        setReferencedBy(refs);
      } else {
        setError(e?.body?.message || e?.message || 'Delete failed');
      }
    }
    setDeleting(false);
  };

  return (
    <Dialog open onClose={onClose} data-testid={`alias-delete-dialog-${name}`}>
      <DialogTitle>{referencedBy ? 'Alias in use' : `Delete ${name}?`}</DialogTitle>
      <DialogContent>
        {referencedBy ? (
          <>
            <Typography gutterBottom>
              This alias is still referenced by the following. Deleting will leave them pointing
              at an alias that no longer exists unless you force the delete.
            </Typography>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {referencedBy.map((ref) => (
                <li key={ref} style={{ fontSize: 13 }}>{ref}</li>
              ))}
            </ul>
          </>
        ) : (
          <Typography>{`Delete the alias "${name}"? This cannot be undone.`}</Typography>
        )}
        {error && <div className='adv-config-msg-err' style={{ marginTop: 10 }}>{error}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button
          data-testid={referencedBy ? `alias-force-delete-${name}` : `alias-confirm-delete-${name}`}
          color='secondary'
          variant='contained'
          disabled={deleting}
          onClick={() => doDelete(!!referencedBy)}
        >
          {referencedBy ? 'Force delete' : (deleting ? 'Deleting…' : 'Delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AliasesScreen;
